import { CountryCode } from '@glow/common'
import { formikValidateNumberInRange, formikValidationNumber, formikValidationOptionalFn } from '@glow/validators'
import i18next from 'i18next'
import { Map } from 'immutable'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AUTOSUGGEST_CUSTOMERS } from '../../../actions/actionTypes'
import { Customer } from '../../../domain/customer'
import { allDepartmentsFromStateSelector } from '../../../selectors/departmentsSelector'
import {
  CustomerAlystraId,
  FlexType,
  ServiceDeliveryType,
  ServiceMappingType,
  ServiceMatrixIdType,
  UpsalesRulesProps
} from '../../../types/coreEntitiesTypes'
import { AppStateType } from '../../../utils/appStateReduxStore'
import { Country } from '../../../utils/country'

const MINUTES_UNTIL_DELIVERY_RANGE_FROM = 15
const MINUTES_UNTIL_DELIVERY_RANGE_TO = 120

export type ServiceMatrixFormProps = {
  id: ServiceMatrixIdType
  customerAlystraId: CustomerAlystraId
  pickupTimeInSecs: string
  timeWindowInMinutes: string
  deliveryTimeInSecs: string
  serviceCode: string
  allowFlexDelivery: boolean
  flexType: FlexType
  internalNameText: string
  internalDescriptionText: string
  externalNameText: string
  externalDescriptionText: string
  serviceMappingType: ServiceMappingType
  numberOfMinutes: string
  level: ServiceMatrixLevel
  pickupPreparationTime?: string
  deliveryPreparationTime?: string
  showExactEtaComponent: boolean
  etaSetting: 'stopsLeftSelected' | 'minutesLeftSelected' | null
  sendAutomaticEtaSms: boolean
  serviceDeliveryType: ServiceDeliveryType | null | ''
  allowOffered: boolean
  pickupDurationSecondsApartment?: number | null | ''
  deliveryDurationSecondsApartment?: number | null | ''
}

export const serviceDeliveryTypes = ['Delivery', 'Return', 'Pickup', 'PickupPoint']

export type ServiceMatrixLevel = 'service' | 'customerService'

export const numberOfMinutesValidator = (value: string, formValues: ServiceMatrixFormProps): string | undefined =>
  formikValidationOptionalFn(
    (value: string) =>
      formikValidationNumber(value, i18next.t('error.notAValidNumber')) ||
      (formValues.etaSetting === 'minutesLeftSelected'
        ? formikValidateNumberInRange(
            Number(value),
            MINUTES_UNTIL_DELIVERY_RANGE_FROM,
            MINUTES_UNTIL_DELIVERY_RANGE_TO,
            i18next.t('serviceMapping.minutesNotInRange', {
              from: MINUTES_UNTIL_DELIVERY_RANGE_FROM,
              to: MINUTES_UNTIL_DELIVERY_RANGE_TO
            })
          )
        : undefined)
  )(value)

export type FilterOptionCode = 'name' | 'country' | 'department' | 'customer'

export const useFilteredServiceUpsalesRules = (
  upsalesRules: UpsalesRulesProps[],
  filterOptionCode: FilterOptionCode,
  searchString: string,
  isLoading: boolean,
  upsalesRuleDeleted: boolean
) => {
  const allDepartments = useSelector((state: AppStateType) => allDepartmentsFromStateSelector(state))

  const searchedCustomers: Map<string, Customer> = useSelector(
    (state: AppStateType) => state.getIn(['searchReducer', AUTOSUGGEST_CUSTOMERS]) ?? Map()
  )

  return useMemo(() => {
    if (searchString.length <= 2) return upsalesRules
    switch (filterOptionCode) {
      case 'name':
        return upsalesRules?.filter((upsalesRule) =>
          upsalesRule.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      case 'country':
        return upsalesRules?.filter((upsalesRule) =>
          Country[upsalesRule.country as keyof typeof Country]
            .toLocaleLowerCase()
            .includes(searchString.toLocaleLowerCase())
        )
      case 'department': {
        const filteredDepartments = allDepartments.filter((d) =>
          d.get('name').toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
        return upsalesRules?.filter((upsalesRule) =>
          upsalesRule.departments?.some((departmentId) => Boolean(filteredDepartments.get(departmentId)))
        )
      }
      case 'customer': {
        const filteredCustomers = searchedCustomers.filter(
          (x) =>
            x.get('name')?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
            x.get('alystraSubcustomer').toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
            x.get('alystraId').includes(searchString)
        )
        return upsalesRules?.filter((upsalesRule) =>
          upsalesRule.customers?.some((customerId) => Boolean(filteredCustomers.get(customerId + '')))
        )
      }
      default:
        return upsalesRules
    }
  }, [upsalesRules, searchString, filterOptionCode, allDepartments, searchedCustomers, isLoading, upsalesRuleDeleted])
}

export const countriesForUpsales = [CountryCode.NO, CountryCode.SE, CountryCode.DK]
